"use client";

/**
 * Barre collee en haut de page quand un siege porte l'identite d'une de ses
 * agences (« Voir comme cette agence »).
 *
 * POURQUOI ELLE EXISTE. Le rappel ne vivait que sur /my-network — la page d'ou
 * la prise de main se declenche, et que `beginActing` quitte aussitot par un
 * rechargement vers « / ». Autrement dit : au moment meme ou le siege commence
 * a agir sous une autre identite, le seul ecran qui le lui disait disparaissait.
 * Il naviguait ensuite dans tout le site sans rien qui distingue le compte de
 * l'agence du sien, et sans moyen de sortir autre que retrouver /my-network.
 *
 * Meme role que la barre d'impersonation admin, et montee au meme endroit dans
 * la coquille : en FLUX, pas en `position: fixed`, pour que le reste du site
 * descende au lieu d'etre recouvert au defilement.
 *
 * Les DEUX barres peuvent s'empiler — un administrateur en prise de controle
 * sur un siege qui agit ensuite pour une de ses agences. C'est pour cela que
 * celle-ci ne reprend pas le bordeaux de l'autre : deux couches, deux couleurs,
 * et chaque bouton de sortie ne defait que sa propre couche.
 */

import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/app/lib/reducers/api";
import {
  readActingState,
  endActing,
  type NetworkActingState,
} from "@/app/lib/networkActing";

const NetworkActingBanner: FC = () => {
  const { t } = useTranslation("guydeez");
  // Lu au montage, jamais pendant le rendu : `readActingState` touche le
  // localStorage, absent au rendu serveur. Une lecture directe ferait diverger
  // le premier rendu client du HTML recu.
  const [acting, setActing] = useState<NetworkActingState | null>(null);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    setActing(readActingState());
  }, []);

  if (!acting) return null;

  /**
   * Sortir, dans cet ordre precis.
   *
   * `endActing()` D'ABORD : il remet les jetons du siege en place, et la route
   * `/network/stop-acting` est justement reservee a un siege agissant pour
   * LUI-MEME (requireHeadOffice refuse une session qui porte une agence).
   * Appelee avant, elle repondrait 403.
   *
   * La fermeture de la trace d'audit est ensuite au mieux : un echec reseau ne
   * doit jamais laisser le siege enferme dans le compte de son agence.
   */
  const stop = async () => {
    if (exiting) return;
    setExiting(true);
    const state = readActingState();
    endActing();
    try {
      if (state) await api.post("/api/network/stop-acting", { actingLogUuid: state.actingLogUuid });
    } catch {
      /* la session du siege est deja restauree */
    }
    if (typeof window !== "undefined") {
      // Rechargement complet, comme a l'entree : chaque tranche du magasin a
      // ete hydratee pour l'agence et doit l'etre a nouveau pour le siege.
      window.location.href = "/my-network";
    }
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        background: "var(--g-ink-900)",
        color: "white",
        padding: "10px 18px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 14,
        fontSize: 13,
        boxShadow: "0 2px 6px rgba(0,0,0,.18)",
        position: "relative",
        zIndex: 60,
      }}
    >
      <div className="flex items-center" style={{ gap: 10, minWidth: 0 }}>
        <span
          aria-hidden="true"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: "rgba(255,255,255,.18)",
            border: "1px solid rgba(255,255,255,.35)",
            fontSize: 14,
            fontWeight: 800,
            flexShrink: 0,
          }}
        >
          🏢
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, lineHeight: 1.2 }}>
            {t("network_acting_banner", { defaultValue: "Vous agissez au nom de" })}{" "}
            <span style={{ textDecoration: "underline" }}>
              {acting.memberName || acting.memberUuid}
            </span>
          </div>
          <div style={{ fontSize: 11, opacity: 0.88, marginTop: 2 }}>
            {t("network_acting_limits", {
              defaultValue:
                "Les moyens de paiement, l’identité du compte et son appartenance au réseau restent hors de portée.",
            })}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={stop}
        disabled={exiting}
        style={{
          flexShrink: 0,
          height: 36,
          padding: "0 16px",
          borderRadius: 999,
          background: "white",
          color: "var(--g-ink-900)",
          border: 0,
          fontWeight: 700,
          fontSize: 12,
          cursor: exiting ? "wait" : "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {exiting
          ? (t("impersonation_exiting", { defaultValue: "Sortie…" }) as string)
          : (t("network_acting_stop", { defaultValue: "Revenir à mon compte" }) as string)}
      </button>
    </div>
  );
};

export default NetworkActingBanner;
